/**
 * 구 JIS를 이용한 계산 방법임
 */

import styled from 'styled-components';

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeBody } from '../../../modules/conveyor/conveyorWriteSlice';

import { Input, Output, Select } from '../common/SingleInput';
import { MultiOutput } from '../common/MultiInput';
import { LoadNoLoadLabel } from '../common/LoadNoLoadLabel';

import { beltCarcassType /*selectMotor*/ } from './library-jis';
import { getDiaRelatedValue } from './library-pipe-conveyor';
import CopyFromOhtersButton from './CopyFromOhtersButton';
import { getIdlerInfo } from '../../../lib/data/idler-data';

import ModalLabel from '../../common/ModalLabel';
import CalcPipeSectionArea from './CalcPipeSectionArea';
import AlertLabel from '../../common/AlertLabel';
import ModalOpenButton from '../common/ModalOpenButton';
// import HorizontalCurveResistance from './HorizontalCurveResistance';
import HorizontalCurveTensionModal from './HorizontalCurveTensionModal';

const PipeConveyorPowerCalculationOldJIS = ({ isValidTension }) => {
    const dispatch = useDispatch();

    const inputPrev = useSelector((state) => state.conveyorWrite.body.input);
    const outputPrev = useSelector((state) => state.conveyorWrite.body.output);
    const resistancePrev = useSelector((state) => state.conveyorWrite.body.resistance);
    const powerPrev = useSelector((state) => state.conveyorWrite.body.power);
    // tightSideTension과 slackSideTension이 계산되었는지 점검
    // const isValidTension = useSelector((state) => state.conveyorWrite.isValidTension);

    const [input, setInput] = useState(
        inputPrev.capacity
            ? inputPrev
            : {
                  capacity: 1000,
                  length: 100,
                  height: 20,
                  materialConveyed: 'Coal',
                  specificGravity: 830,
                  beltSpeed: 4.17,
                  carcassType: 'EP',
                  horizontalCurveAngle: 28,
                  artificialFrictionCoeff: 0.03,
                  carrySpacing: 1.2,
                  returnSpacing: 2.4,
                  initialSpeed: 0.0,
                  skirtLength: 5.0,
                //   carryTensionAtCurveNoLoad: 43948.8,
                //   carryTensionAtCurveLoad: 60527.7,
                //   returnTensionAtCurveNoLoad: 35217.9,
                //   returnTensionAtCurveLoad: 35217.9,
              },
    );

    const language = useSelector((state) => state.language);
    const isEN = language.language === 'EN' ? true : false;
    const isJP = language.language === 'JP' ? true : false;
    // const isKR = language.language === 'KR' ? true : false;

    const [output, setOutput] = useState(outputPrev || {});
    const [resistance, setResistance] = useState(resistancePrev || {});
    const [power, setPower] = useState(powerPrev || {});
    // const [tension, setTension] = useState({});
    // const [belt, setBelt] = useState({});

    const [isOpenAreaCalcModal, setIsOpenAreaCalcModal] = useState(false);
    // const [isOpenPipeDia, setIsOpenPipeDia] = useState(false);

    /* 다른 컨베이어 입력 가져오기 테스트 구간
    
        상기 setInput을 props로 하여 <CopyFromOthersButton setInput={setInput} /> 호출

    다른 컨베이어 입력 가져오기 테스트 구간 */

    const handleChange = (e) => {
        const { name, value } = e.target;
        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    /* Required Section Area */
    useEffect(() => {
        const Q = Number(input.capacity); // Number함수는 숫자로 변환할 수 있는 경우 숫자로 변환하여 반환. null이나 빈문자는 0. true는 1, false는 0. 그 외는 NaN 반환.
        const 𝜌 = Number(input.specificGravity);
        const v = Number(input.beltSpeed);

        const Iv = Q / (3.6 * 𝜌); // m3/sec
        const area = Iv / v; // m2;

        setInput((prev) => ({
            ...prev,
            requiredSectionArea: Math.ceil(area * 100000) / 100000,
        }));
    }, [input.capacity, input.specificGravity, input.beltSpeed]);

    /* Length Correction Factor 계산 */
    useEffect(() => {
        if (!input.artificialFrictionCoeff) return;

        const _lengthCoeff = 0.77931 / (Number(input.artificialFrictionCoeff) - 0.006436) + 15.93;

        setOutput((prev) => ({
            ...prev,
            lengthCoeff: Math.round(_lengthCoeff * 10) / 10,
        }));
    }, [input.artificialFrictionCoeff]);

    /* Slope Angle 계산 */
    useEffect(() => {
        if (!input.length || !input.height) return;

        const _slopeAngle = (Math.atan(input.height / input.length) * 180) / Math.PI;
        setOutput((prev) => ({
            ...prev,
            slopeAngle: Math.round(_slopeAngle * 100) / 100,
        }));
    }, [input.height, input.length]);

    /* Belt, Idler 단위 중량 산정 */
    useEffect(() => {
        if (!input.carrySpacing || !input.returnSpacing || !input.pipeDiameter) return;

        const _beltThick = getDiaRelatedValue(input.pipeDiameter, 'beltThick');
        const _massBelt = getDiaRelatedValue(input.pipeDiameter, 'beltMass');

        const _rotatingMassCarry = getDiaRelatedValue(input.pipeDiameter, 'rotatingMassCarry');
        const _rotatingMassReturn = getDiaRelatedValue(input.pipeDiameter, 'rotatingMassReturn');
        const _massCarryIdler = Math.round((_rotatingMassCarry / input.carrySpacing) * 10) / 10;
        const _massReturnIdler = Math.round((_rotatingMassReturn / input.returnSpacing) * 10) / 10;

        setOutput((prev) => ({
            ...prev,
            massCarryIdler: _massCarryIdler,
            massReturnIdler: _massReturnIdler,
            massBelt: _massBelt,
            beltThick: _beltThick,
        }));
    }, [input.carrySpacing, input.returnSpacing, input.pipeDiameter]);

    /* Pipe Conveyor - 운반물 단면적 계산 */
    useEffect(() => {
        if (!input.capacity || !input.specificGravity || !input.beltSpeed) {
            return;
        }
        const Q = Number(input.capacity); // Number함수는 숫자로 변환할 수 있는 경우 숫자로 변환하여 반환. null이나 빈문자는 0. true는 1, false는 0. 그 외는 NaN 반환.
        const 𝜌 = Number(input.specificGravity);
        const v = Number(input.beltSpeed);

        const isValidInput = (value) => !isNaN(value) && value > 0;

        if (isValidInput(Q) && isValidInput(𝜌) && isValidInput(v)) {
            const Iv = Q / (3.6 * 𝜌); // m3/sec
            const area = Iv / v; // m2

            setOutput((prev) => ({
                ...prev,
                sectionAreaRequired: Math.ceil(area * 100000) / 100000,
            }));
        }
    }, [input.beltSpeed, input.specificGravity, input.capacity]);

    /* Volumetric Capacity, Mass Material */
    useEffect(() => {
        if (!input.capacity || !input.beltSpeed) return;

        const volumetricCapacity =
            Math.round(((1000 * input.capacity) / (3600 * input.specificGravity)) * 10000) / 10000;
        const massMaterial = Math.round(((volumetricCapacity * input.specificGravity) / input.beltSpeed) * 10) / 10;
        setOutput((prev) => ({
            ...prev,
            volumetricCapacity,
            massMaterial,
        }));
    }, [input.beltSpeed, input.capacity, input.specificGravity]);

    /* 동력 계산 (구 JIS인 경우) */
    useEffect(() => {
        // 변수 변환
        const f = Number(input.artificialFrictionCoeff);
        // const Q = Number(input.capacity);
        const v = Number(input.beltSpeed);
        const v0 = Number(input.initialSpeed);
        const L = Number(input.length);
        const H = Number(input.height);
        const L0 = Number(output.lengthCoeff);
        const D = Number(input.pipeDiameter);
        const g = 9.81;
        const qRO = Number(output.massCarryIdler);
        const qRU = Number(output.massReturnIdler);
        const qB = Number(output.massBelt);
        const qG = Number(output.massMaterial);
        const Iv = Number(output.volumetricCapacity);
        const 𝜌 = Number(input.specificGravity);
        const Ls = Number(input.skirtLength);

        const 𝜓 = Number(input.horizontalCurveAngle);

        // const TCWOL = 43948.8; // Tension at Curve on Carrier Side without Load
        // const TCWL = 60527.7; // Tension at Curve on Carrier Side with Load
        // const TRWOL = 35217.9; // Tension at Curve on Return Side without Load
        // const TRWL = 35217.9; // Tension at Curve on Return Side with Load
        const TCWOL = Number(input.carryTensionAtCurveNoLoad || '0');
        const TCWL = Number(input.carryTensionAtCurveLoad || '0');
        const TRWOL = Number(input.returnTensionAtCurveNoLoad || '0');
        const TRWL = Number(input.returnTensionAtCurveLoad || '0');
        // console.log('TRWOL : ', TRWOL);
        // console.log('TRWL : ', TRWL);

        const w = qRO + qRU + 2 * qB;

        // 동력 계산
        const N1 = (f * g * w * v * (L + L0)) / 1000; // 무부하 동력
        const N11 = (((f * g * v * 2 * D) / 75) * L) / 1000; // 벨트발발저항 동력
        const N12 = (g * v * (0.2 * D + 25) * 2) / 1000; // 벨트 조임저항 동력
        const N13 = (f * v * (TCWOL + TRWOL) * 𝜓 * Math.PI) / 180 / 1000; // 무부하 커브부 저항
        const N14 = (f * v * (TCWL + TRWL) * 𝜓 * Math.PI) / 180 / 1000; // 부하 커브부 저항
        // console.log('𝜓 : ', 𝜓);
        // console.log('N13 : ', N13);
        // console.log('N14 : ', N14);
        const N2 = (f * g * qG * v * (L + L0)) / 1000; // 수평 부하 동력
        const N3 = (g * qG * v * H) / 1000; // 수직 부하 동력(양정)
        const N4 = (60 * v * Ls) / 1000; // 스커트 저항 동력
        const N𝛼 = (((Iv * 𝜌 * (v - v0)) / 1000) * v) / 2; // TODO:

        const NNo = N1 + N11 + N12 + N13 + N4;
        const N = N1 + N11 + N12 + N14 + N2 + N3 + N4 + N𝛼;

        setPower((prev) => ({
            ...prev,
            N1: Math.ceil(N1 * 100) / 100,
            N11: Math.ceil(N11 * 100) / 100,
            N12: Math.ceil(N12 * 100) / 100,
            N13: Math.ceil(N13 * 100) / 100,
            N14: Math.ceil(N14 * 100) / 100,
            N2: Math.ceil(N2 * 100) / 100,
            N3: Math.ceil(N3 * 100) / 100,
            N4: Math.ceil(N4 * 100) / 100,
            N𝛼: Math.ceil(N𝛼 * 100) / 100,
            NNo: Math.ceil(NNo * 100) / 100,
            N: Math.ceil(N * 100) / 100,
        }));
    }, [
        input.artificialFrictionCoeff,
        input.beltSpeed,
        input.capacity,
        input.height,
        input.length,
        output.lengthCoeff,
        input.pipeDiameter,
        input.skirtLength,
        output.massBelt,
        output.massCarryIdler,
        output.massMaterial,
        output.massReturnIdler,
        input.horizontalCurveAngle,
        input.initialSpeed,
        input.specificGravity,
        output.volumetricCapacity,
        input.carryTensionAtCurveNoLoad,
        input.carryTensionAtCurveLoad,
        input.returnTensionAtCurveNoLoad,
        input.returnTensionAtCurveLoad,
    ]);

    /* Resistance 계산 */
    useEffect(() => {
        const N3 = Number(power.N3);
        const kWNoLoad = Number(power.NNo);
        const kWLoad = Number(power.N);
        const kWReverse = N3 - 0.5 * (kWLoad - N3);
        const V = Number(input.beltSpeed);
        const overallResistanceLoad = Math.ceil((kWLoad / V) * 1000);
        const overallResistanceNoLoad = Math.ceil((kWNoLoad / V) * 1000);
        const reverseResistanceLoad = Math.ceil((kWReverse / V) * 1000);

        setResistance((prev) => ({
            ...prev,
            reverse: {
                load: reverseResistanceLoad,
                noLoad: 0,
            },
            overall: {
                load: Math.ceil(overallResistanceLoad),
                noLoad: Math.ceil(overallResistanceNoLoad),
            },
        }));
    }, [input.beltSpeed, power.N, power.N3, power.NNo]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        dispatch(
            changeBody({
                key: 'resistance',
                value: resistance,
            }),
        );
    }, [dispatch, resistance]);

    /* 아이들러 수량, 중량 정보 */
    useEffect(() => {
        if (
            !input.beltWidth ||
            !input.carrySpacing ||
            !input.returnSpacing ||
            !input.length ||
            !input.height ||
            !input.skirtLength
        )
            return;

        const idlers = getIdlerInfo(
            input.beltWidth,
            input.carrySpacing,
            input.returnSpacing,
            input.length,
            input.height,
            input.skirtLength,
        );

        // 리덕스에 쓰기
        dispatch(
            changeBody({
                key: 'idlers',
                value: idlers,
            }),
        );
    }, [
        dispatch,
        input.beltWidth,
        input.carrySpacing,
        input.height,
        input.length,
        input.returnSpacing,
        input.skirtLength,
    ]);

    /* 리덕스에 쓰기 */
    useEffect(() => {
        dispatch(
            changeBody({
                key: 'input',
                value: input,
            }),
        );
    }, [dispatch, input]);

    useEffect(() => {
        dispatch(
            changeBody({
                key: 'output',
                value: output,
            }),
        );
    }, [dispatch, output]);

    /* Horizontal Curve 입력 모달 관련 */
    const [isOpenModal, setIsOpenModal] = useState(false);
    // const [isActive, setIsActive] = useState(false);

    return (
        <Wrapper>
            <InputSection>
                <Title>
                    <h3 style={{ marginBottom: '-0.5rem' }}>{isEN ? 'Input Section' : isJP ? '入力部' : '입력부'}</h3>
                </Title>
                <Content>
                    <CopyFromOhtersButton setInput={setInput} />

                    <Input
                        label={isEN ? 'Capacity' : isJP ? '運搬量（重量）' : '운반량(중량)'}
                        variable="Q"
                        unit="tph"
                        variableName="capacity"
                        value={input.capacity}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Horizontal Length' : isJP ? '水平機長' : '수평 길이'}
                        variable="L"
                        unit="m"
                        variableName="length"
                        value={input.length}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Elevation Height' : isJP ? '揚程' : '수직 높이'}
                        variable="H"
                        unit="m"
                        variableName="height"
                        value={input.height}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Material Conveyed' : isJP ? '運搬物名称' : '운반물'}
                        variable="-"
                        unit="-"
                        variableName="materialConveyed"
                        value={input.materialConveyed}
                        onChange={handleChange}
                    />
                    <Input
                        label={
                            <AlertLabel
                                label={isEN ? 'Apparent Specific Gravity of Material' : isJP ? '嵩比重' : '겉보기 비중'}
                                message={
                                    isEN
                                        ? 'Apparent Specific Gravity(or Bulk Density) of Material'
                                        : isJP
                                        ? '個々の粒子の固有比重ではなく、バルク状態の見かけ比重(嵩比重)'
                                        : '개별 입자의 고유 비중이 아닌 벌크 상태의 겉보기 비중'
                                }
                            />
                        }
                        variable="⍴"
                        unit="kg/m3"
                        variableName="specificGravity"
                        value={input.specificGravity}
                        onChange={handleChange}
                    />
                    <Input
                        label={isEN ? 'Belt Speed' : isJP ? 'ベルト速度' : '벨트 속도'}
                        variable="v"
                        unit="m/s"
                        variableName="beltSpeed"
                        value={input.beltSpeed}
                        onChange={handleChange}
                    />
                    {/* <Output
                        label="Required M'tl Section Area in Pipe"
                        variable="-"
                        variableName="sectionAreaRequired"
                        unit="m2"
                        value={output.sectionAreaRequired}
                    /> */}
                    <Input
                        label={
                            isEN
                                ? "Required M'tl Section Area in Pipe"
                                : isJP
                                ? '実積載断面積'
                                : '실적재 단면적(필요 단면적)'
                        }
                        variable="-"
                        unit="m2"
                        variableName="requiredSectionArea"
                        value={input.requiredSectionArea}
                        onChange={handleChange}
                    />
                    <Output
                        // label="Pipe Diameter"
                        label={
                            <ModalLabel
                                label={isEN ? 'Pipe Diameter' : isJP ? 'パイプ径' : '파이프 직경'}
                                onClick={() => setIsOpenAreaCalcModal(true)}
                            />
                        }
                        variable="Dp"
                        unit="mm"
                        variableName="pipeDiameter"
                        value={input.pipeDiameter}
                    />
                    {isOpenAreaCalcModal && (
                        <CalcPipeSectionArea
                            isOpenModal={isOpenAreaCalcModal}
                            setIsOpenModal={setIsOpenAreaCalcModal}
                            parentState={input}
                            setParentState={setInput}
                        />
                    )}
                    {/* {isOpenPipeDia && <SelectPipeDia requiredSectionArea={output.sectionAreaRequired} setParentState={setInput} setIsOpenModal={setIsOpenPipeDia} />} */}
                    <Output
                        label={isEN ? 'Belt Width' : isJP ? 'ベルト幅' : '벨트 폭'}
                        variable="B"
                        unit="mm"
                        variableName="beltWidth"
                        value={input.beltWidth}
                    />
                    <Input
                        label={
                            isEN
                                ? 'Angle of Curvature at Curved Section'
                                : isJP
                                ? 'PC曲がり範囲角度'
                                : '파이프컨베이어 수평커브 각도'
                        }
                        variable="𝝍"
                        unit="deg."
                        variableName="horizontalCurveAngle"
                        value={input.horizontalCurveAngle}
                        onChange={handleChange}
                    />
                    <Select
                        label={isEN ? 'Belt Carcass Type' : isJP ? 'ベルト芯体種別' : '벨트 심체의 종류'}
                        variable="-"
                        unit="-"
                        variableName="carcassType"
                        value={input.carcassType}
                        options={beltCarcassType}
                        onChange={handleChange}
                    />
                    <Input
                        label={
                            isEN
                                ? 'Artificial Friction Coefficient'
                                : isJP
                                ? 'ローラー回転摩擦係数'
                                : '롤러의 회전부 마찰계수'
                        }
                        variable="f"
                        unit="-"
                        variableName="artificialFrictionCoeff"
                        value={input.artificialFrictionCoeff}
                        onChange={handleChange}
                        referenceValue={0.02}
                    />
                    {/* <Input
                        label="Number of Pulleys"
                        variable="-"
                        unit="sets"
                        variableName="qtyPulley"
                        value={input.qtyPulley}
                        onChange={handleChange}
                        referenceValue={6}
                    /> */}
                    <Input
                        label={
                            <AlertLabel
                                label={
                                    isEN
                                        ? 'Installation Spacing of Carrying Idlers(Panel)'
                                        : isJP
                                        ? 'パネルーピッチ(キャリヤ)'
                                        : '판넬 피치(캐리어)'
                                }
                                // message={<StandardPanelPitch />}
                                message="~ BW800 : 1.0m\nBW900 ~ : 1.2m"
                            />
                        }
                        variable="a0"
                        unit="m"
                        variableName="carrySpacing"
                        value={input.carrySpacing}
                        onChange={handleChange}
                        referenceValue={1.0}
                    />
                    <Input
                        label={
                            <AlertLabel
                                label={
                                    isEN
                                        ? 'Installation Spacing of Return Idlers(Panel)'
                                        : isJP
                                        ? 'パネルーピッチ(レタン)'
                                        : '판넬 피치(리턴)'
                                }
                                // message={<StandardPanelPitch />}
                                message="~ BW800 : 2.0m\nBW900 ~ : 2.4m"
                            />
                        }
                        variable="au"
                        unit="m"
                        variableName="returnSpacing"
                        value={input.returnSpacing}
                        onChange={handleChange}
                        referenceValue={3.0}
                    />
                    <Input
                        label={isEN ? 'Skirtplate Length' : isJP ? 'スカートの長さ' : '스커트 길이'}
                        variable="Ls"
                        unit="m"
                        variableName="skirtLength"
                        value={input.skirtLength}
                        onChange={handleChange}
                    />
                    {/* <Input
                        label="Trough Angle"
                        variable="𝜆" // lamda
                        unit="deg."
                        variableName="troughAngle"
                        value={input.troughAngle}
                        onChange={handleChange}
                        referenceValue={30}
                    /> */}
                    <Input
                        label={
                            <AlertLabel
                                label={
                                    isEN
                                        ? 'Initial Speed of Material Handled'
                                        : isJP
                                        ? '運搬物のベルト進行方向速度'
                                        : '운반물의 벨트진행 방향 속도'
                                }
                                message={
                                    isJP
                                        ? '積載点での 運搬物の ベルト進行方向に対する速度。\n運搬物がベルトに垂直に落下する場合、速度は「0」となる。'
                                        : isEN
                                        ? "The speed of the material in the belt's direction of travel at the Loading Point.\nIf the material falls perpendicularly onto the belt, the speed is '0'."
                                        : "Loading Point에서 운반물의 벨트 진행방향에 대한 속도.\n운반물이 벨트에 수직으로 떨어지는 경우에는 '0'이다."
                                }
                            />
                        }
                        variable="v0"
                        unit="m/s"
                        variableName="initialSpeed"
                        value={input.initialSpeed}
                        onChange={handleChange}
                        referenceValue={0.0}
                    />
                    {/* <Input
                        label="Front Tile Angle (Angle at Belt Plane)"
                        variable="𝜀" // epsilon
                        unit="deg."
                        variableName="tiltAngle"
                        value={input.tiltAngle}
                        onChange={handleChange}
                        referenceValue={0.0}
                        disabled={true}
                    /> */}
                    {/* <Input
                        label="Conveyor Length of Front Tilt Type Idlers Part"
                        variable="L𝜀" // Lepsilon
                        unit="m"
                        variableName="tiltLength"
                        value={input.tiltLength}
                        onChange={handleChange}
                        referenceValue={0.0}
                        disabled={true}
                    /> */}
                    {/* <Input
                        label="Friction Coefficient between the Idlers and the Belt"
                        variable="𝜇0"
                        unit="-"
                        variableName="frictionCoeffIdlerBelt"
                        value={input.frictionCoeffIdlerBelt}
                        onChange={handleChange}
                        referenceValue={0.4}
                    />
                    <Input
                        label="Friction Coefficient between the Mateiral and the Belt"
                        variable="𝜇1"
                        unit="-"
                        variableName="frictionCoeffMaterialBelt"
                        value={input.frictionCoeffMaterialBelt}
                        onChange={handleChange}
                        referenceValue={0.7}
                    />
                    <Input
                        label="Friction Coefficient between the Belt and the Skirtplate"
                        variable="𝜇2"
                        unit="-"
                        variableName="frictionCoeffBeltSkirt"
                        value={input.frictionCoeffBeltSkirt}
                        onChange={handleChange}
                        referenceValue={0.7}
                    />
                    <Input
                        label="Friction Coefficient between the Belt and the Cleaner"
                        variable="𝜇3"
                        unit="-"
                        variableName="frictionCoeffBeltCleaner"
                        value={input.frictionCoeffBeltCleaner}
                        onChange={handleChange}
                        referenceValue={0.5}
                    />
                    <Input
                        label="Q'ty of (External) Belt Cleaner"
                        variable="-"
                        unit="set(s)"
                        variableName="qtyBeltCleaner"
                        value={input.qtyBeltCleaner}
                        onChange={handleChange}
                        referenceValue={2}
                    />
                    <Input
                        label="Pressure between the Belt and the Cleaner"
                        variable="P"
                        unit="N/m2"
                        variableName="cleanerContactPressure"
                        value={input.cleanerContactPressure}
                        onChange={handleChange}
                        referenceValue={65000}
                    />
                    <Input
                        label="Q'ty of Unloading Scraper"
                        variable="-"
                        unit="set(s)"
                        variableName="qtyUnloadingScraper"
                        value={input.qtyUnloadingScraper}
                        onChange={handleChange}
                    />
                    <Input
                        label="Scraping Coefficient of Unloading Scraper"
                        variable="ka"
                        unit="N/m"
                        variableName="scrapingCoeff"
                        value={input.scrapingCoeff}
                        onChange={handleChange}
                        referenceValue={1500}
                    /> */}
                    <ModalOpenButton
                        isActive={isValidTension}
                        onClick={() => setIsOpenModal(true)}
                        message={
                            isEN
                                ? 'Input the average belt tension acting on the horizontal curve.\nThe button will be activated if you calculate Tight Side Tension and Slack Side Tension in the Drive calculation below.'
                                : isJP
                                ? '水平カーブ部に作用する平均ベルトテンションを入力します。\n下のドライブ計算で緊張側張力と弛緩側張力を計算すると、ボタンがアクティブになります。'
                                : '수평 커브부에 작용하는 평균 벨트텐션을 입력합니다.\n아래 Drive 계산에서 Tight Side Tension 및 Slack Side Tension을 계산하면 버튼이 활성화됩니다. '
                        }
                    >
                        {isEN
                            ? 'Input Average Tension @Horizontal Curve'
                            : isJP
                            ? '水平カーブ部平均張力入力'
                            : '수평커브부 평균장력 입력'}
                    </ModalOpenButton>
                    {isOpenModal && (
                        <HorizontalCurveTensionModal
                            visible={isOpenModal}
                            setVisible={setIsOpenModal}
                            input={input}
                            setInput={setInput}
                        />
                    )}
                    <LoadNoLoadLabel />
                    <MultiOutput
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Carrier Side)'
                                : isJP
                                ? '水平カーブ部平均張力(キャリヤ側)'
                                : '수평 커브부 평균장력(캐리어측)'
                        }
                        variable=""
                        unit="N"
                        variableName="carryTensionAtCurveNoLoad"
                        values={[input.carryTensionAtCurveLoad, input.carryTensionAtCurveNoLoad]}
                    />
                    <MultiOutput
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Return Side)'
                                : isJP
                                ? '水平カーブ部平均張力(リタン側)'
                                : '수평 커브부 평균장력(리턴측)'
                        }
                        variable=""
                        unit="N"
                        variableName="returnTensionAtCurveLoad"
                        values={[input.returnTensionAtCurveLoad, input.returnTensionAtCurveNoLoad]}
                    />
                    {/* <Output
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Carrier Side, No Load)'
                                : isJP
                                ? '水平カーブ部平均張力(キャリヤ側, No Load)'
                                : '수평 커브부 평균장력(캐리어측, No Load)'
                        }
                        variable="A"
                        unit="N"
                        variableName="carryTensionAtCurveNoLoad"
                        value={input.carryTensionAtCurveNoLoad}
                        // onChange={handleChange}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Carrier Side, Load)'
                                : isJP
                                ? '水平カーブ部平均張力(キャリヤ側, Load)'
                                : '수평 커브부 평균장력(캐리어측, Load)'
                        }
                        variable="B"
                        unit="N"
                        variableName="carryTensionAtCurveLoad"
                        value={input.carryTensionAtCurveLoad}
                        // onChange={handleChange}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Return Side, No Load)'
                                : isJP
                                ? '水平カーブ部平均張力(リタン側, No Load)'
                                : '수평 커브부 평균장력(리턴측, No Load)'
                        }
                        variable="C"
                        unit="N"
                        variableName="returnTensionAtCurveNoLoad"
                        value={input.returnTensionAtCurveNoLoad}
                        // onChange={handleChange}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Average Tension @Horizontal Curve(Return Side, Load)'
                                : isJP
                                ? '水平カーブ部平均張力(リタン側, Load)'
                                : '수평 커브부 평균장력(리턴측, Load)'
                        }
                        variable="D"
                        unit="N"
                        variableName="returnTensionAtCurveLoad"
                        value={input.returnTensionAtCurveLoad}
                        // onChange={handleChange}
                    /> */}
                </Content>
            </InputSection>
            <InterCalcSection>
                <Title>
                    <h3>{isEN ? 'Internal Calculation' : isJP ? '内部計算結果' : '내부계산 결과'}</h3>
                </Title>
                <Content>
                    <Output
                        // label="Length Correction Factor "
                        label={isEN ? 'Length Correction Factor' : isJP ? '中心距離修正値' : '중심거리 보정계수'}
                        variable="Lo"
                        variableName="lengthCoeff"
                        unit="-"
                        value={output.lengthCoeff}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Mass of Rotating Parts of Idler along the Carrying Side'
                                : isJP
                                ? 'パイプ部ローラー回転部重量(キャリヤ)'
                                : '롤러회전부 중량(캐리어측)'
                        }
                        variable="qRO"
                        variableName="massCarryIdler"
                        unit="kg/m"
                        value={output.massCarryIdler}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Mass of Rotating Parts of Idler along the Return Side'
                                : isJP
                                ? 'パイプ部ローラー回転部重量(リタン)'
                                : '롤러회전부 중량(리턴측)'
                        }
                        variable="qRU"
                        variableName="massReturnIdler"
                        unit="kg/m"
                        value={output.massReturnIdler}
                    />
                    <Output
                        label={isEN ? 'Mass of Belt by JIS' : isJP ? 'ベルト重量(JIS基準)' : '벨트 중량(JIS기준)'}
                        variable="qB"
                        variableName="massBelt"
                        unit="kg/m"
                        value={output.massBelt}
                    />
                    {/* <Output
                        label="Belt Thickness by JIS"
                        variable="d"
                        variableName="beltThick"
                        unit="mm"
                        value={output.beltThick}
                    /> */}
                    {/* <Output
                        label={
                            <AlertLabel
                                label={isEN ? 'Belt Overlap Angle' : isJP ? 'ベルト重ね角' : '벨트 중첩각'}
                                message="ベルト重ね角は。。。。。。。説明はここで。。。"
                            />
                        }
                        variable="-"
                        variableName="beltOverlapAngle"
                        unit="deg"
                        value={output.beltOverlapAngle}
                    /> */}
                    <Output
                        label={isEN ? 'Volumetric Capacity' : isJP ? '運搬量（容積）' : '운반량(용적)'}
                        variable="Iv"
                        variableName="volumetricCapacity"
                        unit="m3/s"
                        value={output.volumetricCapacity}
                    />
                    {/* <Output
                        label="Skirtplate Width"
                        variable="b1"
                        variableName="skirtWidth"
                        unit="m"
                        value={output.skirtWidth}
                    /> */}
                    {/* <Output
                        label="Acceleration Area Length(@Skirt)"
                        variable="Ib"
                        variableName="accelerationLength"
                        unit="m"
                        value={output.accelerationLength}
                    /> */}
                    {/* <Input
                        label="Skirtplate Length (Acceleration Length + 2.5m)"
                        variable="l"
                        variableName="skirtLength"
                        unit="m"
                        value={output.skirtLength}
                    /> */}
                    {/* <Input
                        label="Skirtplate Length"
                        variable="l"
                        unit="m"
                        variableName="skirtLength"
                        value={input.skirtLength}
                        onChange={handleChange}
                    /> */}
                    <Output
                        label={isEN ? 'Mass of Material Handled' : isJP ? '運搬物の重量' : '운반물 중량'}
                        variable="qG"
                        variableName="massMaterial"
                        unit="kg/m"
                        value={output.massMaterial}
                    />
                    {/* <Output
                        label="Slope Angle of Conveyor"
                        variable="𝛿"
                        variableName="slopeAngle"
                        unit="deg."
                        value={output.slopeAngle}
                    /> */}
                    {/* <Output
                        label="Trough Angle Coefficient"
                        variable="C𝜀" // Cepsilon
                        variableName="troughAngleCoeff"
                        unit="deg."
                        value={output.troughAngleCoeff}
                    /> */}
                    {/* <Output
                        label="Contact Area of Cleaner with Belt"
                        variable="A"
                        variableName="cleanerContactArea"
                        unit="m2"
                        value={output.cleanerContactArea}
                    /> */}
                </Content>
            </InterCalcSection>
            <PowerSection>
                <Title>
                    <h3>{isEN ? 'Power' : isJP ? '動力' : '동력'}</h3>
                </Title>
                <Content>
                    <Output
                        label={isEN ? 'No Load Power' : isJP ? '無負荷動力' : '무부하 동력(무부하)'}
                        variable="N1"
                        variableName="N1"
                        unit="kW"
                        value={power.N1}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power due to Belt Rebound Resistance'
                                : isJP
                                ? 'ベルトの反撥抵抗動力'
                                : '벨트 반발 저항에 의한 동력(무부하)'
                        }
                        variable="N11"
                        variableName="N11"
                        unit="kW"
                        value={power.N11}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power due to Belt Squeezing Resistance'
                                : isJP
                                ? 'ベルトの絞り抵抗動力'
                                : '벨트 조임 저항에 의한 동력(무부하)'
                        }
                        variable="N12"
                        variableName="N12"
                        unit="kW"
                        value={power.N12}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power Due to Belt Curve Resistance(No Load)'
                                : isJP
                                ? '無負荷ベルトのカーブ部抵抗動力'
                                : '벨트 커브부 저항에 의한 동력(무부하)'
                        }
                        variable="N13"
                        variableName="N13"
                        unit="kW"
                        value={power.N13}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power Due to Belt Curve Resistance(Load)'
                                : isJP
                                ? '負荷ベルトのカーブ部抵抗動力'
                                : '벨트 커브부 저항에 의한 동력(부하)'
                        }
                        variable="N14"
                        variableName="N14"
                        unit="kW"
                        value={power.N14}
                    />
                    <Output
                        label={isEN ? 'Horizontal Load Power' : isJP ? '水平負荷動力' : '수평부하동력(부하)'}
                        variable="N2"
                        variableName="N2"
                        unit="kW"
                        value={power.N2}
                    />
                    <Output
                        label={isEN ? 'Lifting Power' : isJP ? '揚程動力' : '양정동력(부하)'}
                        variable="N3"
                        variableName="N3"
                        unit="kW"
                        value={power.N3}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power Due to Skirtplate Resistance'
                                : isJP
                                ? 'スカート抵抗動力'
                                : '스커트 저항에 의한 동력(무부하)'
                        }
                        variable="N4"
                        variableName="N4"
                        unit="kW"
                        value={power.N4}
                    />
                    <Output
                        label={
                            isEN
                                ? 'Power Due to Material Acceleration'
                                : isJP
                                ? '運搬物加速動力'
                                : '운반물 가속을 위한 동력(부하)'
                        }
                        variable="N𝛼"
                        variableName="N𝛼"
                        unit="kW"
                        value={power.N𝛼}
                    />
                    <Output
                        label={
                            isEN ? 'Overall Power(No Load)' : isJP ? '無負荷合計軸動力' : '무부하 합계 축동력(무부하)'
                        }
                        variable="NNo"
                        variableName="overallWOL"
                        unit="kW"
                        value={power.NNo}
                    />
                    <Output
                        label={isEN ? 'Overall Power(Load)' : isJP ? '負荷合計軸動力' : '부하 합계 축동력(부하)'}
                        variable="N"
                        variableName="overalWL"
                        unit="kW"
                        value={power.N}
                    />
                </Content>
            </PowerSection>
            <ResistanceSection>
                <Title>
                    <h3>{isEN ? 'Resistance' : isJP ? '抵抗' : '저항'}</h3>
                </Title>
                <Content>
                    <LoadNoLoadLabel />
                    {/* <MultiOutput
                        label="Main Resistance"
                        variable="FH"
                        unit="N"
                        variableName="main"
                        values={[resistance?.main?.load, resistance?.main?.noLoad]}
                        // values={[100, 200]}
                    />
                    <MultiOutput
                        label="Secondary Resistance"
                        variable="FN"
                        variableName="secondary"
                        unit="N"
                        values={[resistance?.secondary?.load, resistance?.secondary?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Acceleration Resistance of Material in Acc. Area"
                        variable="FN"
                        variableName="secondary1"
                        unit="N"
                        values={[resistance?.secondary1?.load, resistance?.secondary1?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance between Material and the Skirt Plates in Acc. Area"
                        variable="Ff"
                        variableName="secondary2"
                        unit="N"
                        values={[resistance?.secondary2?.load, resistance?.secondary2?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Wrapping Resistance of Belt on Pulleys"
                        variable="Ft"
                        variableName="secondary3"
                        unit="N"
                        values={[resistance?.secondary3?.load, resistance?.secondary3?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Bearing Resistance of Pulley (Except Driving Pulley)"
                        variable="Ft"
                        variableName="secondary4"
                        unit="N"
                        values={[resistance?.secondary4?.load, resistance?.secondary4?.noLoad]}
                    />
                    <MultiOutput
                        label="Special Main Resistance"
                        variable="FN"
                        variableName="specialMain"
                        unit="N"
                        values={[resistance?.specialMain?.load, resistance?.specialMain?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance when Idlers have tilted frontward"
                        variable="F𝜀" // Fepsilon
                        variableName="specialMain1"
                        unit="N"
                        values={[resistance?.specialMain1?.load, resistance?.specialMain1?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Resistance between Material and Skirtplates outside Acc. Area "
                        variable="FgL"
                        variableName="specialMain2"
                        unit="N"
                        values={[resistance?.specialMain2?.load, resistance?.specialMain2?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Friction Resistance due to Belt Cleaner"
                        variable="Fr"
                        variableName="specialMain3"
                        unit="N"
                        values={[resistance?.specialMain3?.load, resistance?.specialMain3?.noLoad]}
                    />
                    <MultiOutput
                        isSub
                        label="Friction Resistance of Unloading Scraper"
                        variable="Fa"
                        variableName="specialMain4"
                        unit="N"
                        values={[resistance?.specialMain4?.load, resistance?.specialMain4?.noLoad]}
                    />
                    <MultiOutput
                        label="Special Secondary Resistance, if Any"
                        variable="Fs2"
                        variableName="specialSecondary"
                        unit="N"
                        values={[resistance?.specialSecondary?.load, resistance?.specialSecondary?.noLoad]}
                    />
                    <MultiOutput
                        label="Slope Resistance"
                        variable="FST"
                        variableName="slope"
                        unit="N"
                        values={[resistance?.slope?.load, resistance?.slope?.noLoad]}
                    />
                    <MultiOutput
                        label={
                            <AlertLabel
                                label="Horizontal Curve Resistance"
                                message="아래 Tension 계산 후 계산하면 자동으로 입력됩니다.\n以下のテンション計算後に計算すると自動的に入力されます。"
                            />
                        }
                        variable="-"
                        unit="N"
                        variableName="horizontalCurveResistance"
                        values={[horizontalCurveResistance?.load, horizontalCurveResistance?.noLoad]}
                        // values={[5000, 5000]}
                    /> */}
                    <MultiOutput
                        label="Overall Resistance"
                        variable="FU"
                        variableName="overall"
                        unit="N"
                        values={[resistance?.overall?.load, resistance?.overall?.noLoad]}
                    />
                </Content>
            </ResistanceSection>
        </Wrapper>
    );
};

export default PipeConveyorPowerCalculationOldJIS;

/* =================   Styles   ================== */
const Wrapper = styled.div``;
const InputSection = styled.div``;
const InterCalcSection = styled.div``;
const PowerSection = styled.div``;
const ResistanceSection = styled.div``;
const Title = styled.div``;
const Content = styled.div``;
